import { store } from "../app/store";
import { type IBookingReservation, type IBookingProduct } from "../features/booking/bookingInterface";
import {
  type IHotelProduct,
  type IHotelRoomCategory,
  type IHotelRoomOccupancyAvailabilityPricing,
} from "../features/hotel/hotelInterface";
import { findRoomCategory } from "./roomCategory";
import { findPublicRate, getReferencePriceForRate } from "./rate";
import { normalizePricing } from "./mewsPrice";

export const getNormalBedCount = (categoryId: IHotelRoomCategory["Id"]) => {
  const category = findRoomCategory(categoryId);
  if (category) return category.NormalBedCount;
  return 0;
};

export const hasProduct = (id: IBookingProduct["Id"]) => {
  const { booking } = store.getState();
  return booking.Products.some((p) => p.Id === id);
};

export const findProduct = (id: IHotelProduct["Id"]) => {
  const { hotel } = store.getState();
  return hotel.Products.find((p) => p.Id === id);
};

export const findBookingProductDates = (id: IBookingProduct["Id"]) => {
  const { booking } = store.getState();
  const product = booking.Products.find((p) => p.Id === id);
  if (product) {
    return product.Dates;
  }
  return null;
};

export const getPriceByRate = (
  Reservation: IBookingReservation,
): (IHotelRoomOccupancyAvailabilityPricing & {
  ReferencePrice?: IHotelRoomOccupancyAvailabilityPricing["Price"];
}) | null => {
  const { hotel } = store.getState();
  const rca = hotel.RoomCategoryAvailabilities.find(
    (rca) => rca.RoomCategoryId === Reservation.RoomCategoryId,
  );
  if (rca) {
    const roa = rca.RoomOccupancyAvailabilities.find(
      (roa) =>
        roa.AdultCount === Reservation.AdultCount &&
        roa.ChildCount === Reservation.ChildCount,
    );
    if (roa) {
      const pricing = roa.Pricing.find((p) => p.RateId === Reservation.RateId);
      if (!pricing) return null;

      const currencyCode = hotel.Culture.CurrencyCode;
      const normalizedPricing = roa.Pricing.map((item) =>
        normalizePricing(item, currencyCode),
      );
      const normalized = normalizePricing(pricing, currencyCode);
      const publicRate = findPublicRate(Reservation.RateId);
      const publicPricing =
        publicRate && publicRate.Id !== Reservation.RateId
          ? normalizedPricing.find((p) => p.RateId === publicRate.Id)
          : undefined;
      const referencePrice =
        publicPricing?.Price ??
        getReferencePriceForRate(
          normalizedPricing,
          Reservation.RateId,
          currencyCode,
        );

      return {
        ...normalized,
        ...(referencePrice ? { ReferencePrice: referencePrice } : {}),
      };
    }
  }
  return null;
};
